import { memo, useEffect, useMemo, useState } from 'react';
import { ReactComponent as LinkIcon } from '$icons/uncolored/link.svg';
import { ReactComponent as Video } from '$icons/uncolored/video.svg';
import { ReactComponent as ArrowDown } from '$icons/uncolored/arrow.svg';

import { CandidateData } from '$types/candidates';
import { UserType } from '$types/UserTypes';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { FileCategoryType } from '$types/categoryType';
import { segmentTrack } from '$utils/handleSegment';
import SPKSingleEmployerCandidateShowcase from './SPKSingleEmployerCandidateShowcase/SPKSingleEmployerCandidateShowcase';
import SPKEmployerShowcaseVideoModal from './SPKEmployerShowcaseVideoModal/SPKEmployerShowcaseVideoModal';

type SortedAttachmentType = {
  Icon: any;
  Title: string;
  Link?: string;
  BehanceFix?: boolean;
  onClick?: () => void;
  index?: number;
  segmentEvent?: () => void;
};

const SPKEmployerCandidateShowcase = ({
  candidate,
  positionId,
}: {
  candidate: CandidateData;
  positionId: number;
}) => {
  const [showMore, setShowMore] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any>();
  const DISPLAYED_FILE_CATEGORIES_ID = useMemo(() => {
    return [FileCategoryType.REFERENCE, FileCategoryType.WRITING_SAMPLES];
  }, []);
  const [sortedAttachments, setSortedAttachments] = useState<
    SortedAttachmentType[]
  >([]);

  const DisplayedFilesAmmount = 4;

  const toggleItemsVisibilityHandler = () => {
    if (!showMore) {
      segmentTrack('Candidate Show More Clicked', {
        candidate_id: candidate.Id,
        position_id: positionId,
      });
    } else {
      segmentTrack('Candidate Show Less Clicked', {
        candidate_id: candidate.Id,
        position_id: positionId,
      });
    }
    setShowMore(!showMore);
  };

  useEffect(() => {
    let tempSortedAttachments: SortedAttachmentType[] = [];

    if ('ScorecardUrl' in candidate && candidate.ScorecardUrl)
      tempSortedAttachments.push({
        Icon: <LinkIcon className="fill-spk-brightBlue" />,
        Title: 'AI Scorecard (beta)',
        Link: candidate.ScorecardUrl,
        segmentEvent: () => {
          segmentTrack('AI Scorecard Link Clicked', {
            candidate_id: candidate.Id,
            position_id: positionId,
          });
        },
      });

    /** Videos */
    if ('AdditionalVideos' in candidate) {
      candidate.AdditionalVideos.forEach((item) => {
        tempSortedAttachments.push({
          Icon: <Video className="fill-spk-brightBlue" />,
          Title: item.Name,
          onClick: () => {
            setSelectedVideo({
              ...item,
              Name: item.Name,
            });
            segmentTrack('Open Highlight Video Modal', {
              candidate_id: candidate.Id,
              position_id: positionId,
              title: item.Name,
            });
          },
        });
      });
    }

    /** Custom Url */
    if ('CustomUrl' in candidate && candidate.CustomUrl) {
      tempSortedAttachments.push({
        Icon: <LinkIcon className="fill-spk-brightBlue" />,
        Title: `${candidate.User?.FirstName}'s Portfolio`,
        Link: candidate.CustomUrl,
        segmentEvent: () => {
          segmentTrack('Candidate External Link Clicked', {
            candidate_id: candidate.Id,
            target_url: candidate.CustomUrl,
            target: 'Custom',
            position_id: positionId,
          });
        },
      });
    }

    /** Behance Url */
    if ('BehanceUrl' in candidate && candidate.BehanceUrl) {
      tempSortedAttachments.push({
        Icon: <LinkIcon className="fill-spk-brightBlue" />,
        Title: `${candidate.User?.FirstName}'s Behance Portfolio`,
        Link: candidate.BehanceUrl,
        BehanceFix: true,
        segmentEvent: () => {
          segmentTrack('Candidate External Link Clicked', {
            candidate_id: candidate.Id,
            target_url: candidate.BehanceUrl,
            target: 'Behance',
            position_id: positionId,
          });
        },
      });
    }

    /** Github Url */
    if ('GithubUrl' in candidate && candidate.GithubUrl) {
      tempSortedAttachments.push({
        Icon: <LinkIcon className="fill-spk-brightBlue" />,
        Title: `${candidate.User?.FirstName}'s Github`,
        Link: candidate.GithubUrl,
        segmentEvent: () => {
          segmentTrack('Candidate External Link Clicked', {
            candidate_id: candidate.Id,
            target_url: candidate.GithubUrl,
            target: 'Github',
            position_id: positionId,
          });
        },
      });
    }

    if ('Files' in candidate && candidate?.Files) {
      candidate?.Files.forEach((singleFile, index) => {
        if (
          singleFile?.FileCategory &&
          DISPLAYED_FILE_CATEGORIES_ID.indexOf(singleFile?.FileCategory?.Id) !==
            -1 &&
          index <= 1 // display only two files
        ) {
          const isPdf = singleFile.Url?.includes('.pdf') ?? false;
          const pdfViewerUrl = new URL(window.origin);
          pdfViewerUrl.pathname = `pdf-viewer/file/${candidate.Id}/${singleFile.Id}`;
          tempSortedAttachments.push({
            Icon: <LinkIcon className="fill-spk-brightBlue" />,
            Title: `${isPdf ? 'View' : 'Download'} ${
              singleFile.FileCategory.CategoryName
            }`,
            Link: isPdf ? pdfViewerUrl.toString() : singleFile.Url,
            segmentEvent: () => {
              if (singleFile?.FileCategory?.Id === FileCategoryType.REFERENCE) {
                segmentTrack('Reference Download', {
                  candidate_id: candidate.Id,
                  file_id: singleFile.Id,
                });
              } else {
                segmentTrack('Write Assessment Download', {
                  candidate_id: candidate.Id,
                  file_id: singleFile.Id,
                });
              }
            },
          });
        }
      });
    }

    /** Curriculum File */
    if ('Curriculum' in candidate && candidate?.Curriculum?.Url) {
      const cvUrl = new URL(window.origin);
      cvUrl.pathname = `pdf-viewer/cv/${candidate.Id}`;
      tempSortedAttachments.push({
        Icon: <LinkIcon className="fill-spk-brightBlue" />,
        Title: `View ${candidate.User?.FirstName} ${candidate.User?.LastName} CV`,
        Link: cvUrl.toString(),
        segmentEvent: () => {
          segmentTrack('CV Downloaded', {
            candidate_id: candidate.Id,
            file_id: candidate?.Curriculum?.Id,
            position_id: positionId,
          });
        },
      });
    }

    tempSortedAttachments = tempSortedAttachments.map((item, index) => {
      item.index = index;
      return item;
    });

    setSortedAttachments(tempSortedAttachments);
  }, [candidate, DISPLAYED_FILE_CATEGORIES_ID, positionId]);

  return (
    <>
      {selectedVideo && (
        <ModalInTailwind
          isOpen={!!selectedVideo}
          closeModal={() => {
            setSelectedVideo(undefined);
          }}
        >
          <SPKEmployerShowcaseVideoModal
            selectedVideo={selectedVideo}
            setSelectedVideo={setSelectedVideo}
            candidate={candidate}
          />
        </ModalInTailwind>
      )}
      <div className="flex flex-col gap-2">
        {sortedAttachments.map((singleAttachment, index) => {
          if (index < DisplayedFilesAmmount)
            return (
              <SPKSingleEmployerCandidateShowcase
                icon={singleAttachment.Icon}
                title={singleAttachment.Title}
                link={singleAttachment.Link}
                onClick={singleAttachment.onClick}
                key={`${candidate.Id}_attachment_${singleAttachment.index}`}
                segmentEvent={singleAttachment.segmentEvent}
              />
            );
          return (
            <SPKSingleEmployerCandidateShowcase
              additionalClassNames={!showMore ? 'hidden' : 'flex'}
              icon={singleAttachment.Icon}
              title={singleAttachment.Title}
              link={singleAttachment.Link}
              onClick={singleAttachment.onClick}
              key={`${candidate.Id}_attachment_${singleAttachment.index}`}
              segmentEvent={singleAttachment.segmentEvent}
            />
          );
        })}
      </div>
      {DisplayedFilesAmmount < sortedAttachments.length && (
        <button
          type="button"
          onClick={toggleItemsVisibilityHandler}
          className="flex flex-row items-center gap-2 text-xs text-spk-brightBlue font-bold mt-2"
        >
          <ArrowDown
            className={`w-3 h-3 fill-spk-brightBlue ${
              showMore ? 'rotate-180' : 'rotate-0'
            }`}
          />
          Show {!showMore ? ' More' : ' Less'}
        </button>
      )}
    </>
  );
};

SPKEmployerCandidateShowcase.defaultProps = {
  role: UserType.EMPLOYER,
};

export default memo(SPKEmployerCandidateShowcase);
